import { useId, useState } from "react";
import { X } from "lucide-react";
import type { Tag } from "../types";

// Edit a controlled-vocabulary tag set (industries / technologies / skills): removable chips plus an
// add control backed by a datalist of the existing vocabulary. Brand-new vocabulary terms are out of
// scope for v1 (they need a hue/label mapping) — you pick from the defined tags.
export function TagEditor({
  label,
  singular,
  value,
  options,
  onChange,
}: {
  label: string;
  singular: string;
  value: string[];
  options: Tag[];
  onChange: (ids: string[]) => void;
}) {
  const [input, setInput] = useState("");
  const listId = useId();
  const labelFor = (id: string) => options.find((o) => o.id === id)?.label ?? id;
  const byLabel = (txt: string) =>
    options.find((o) => o.label.toLowerCase() === txt.trim().toLowerCase());
  const available = options.filter((o) => !value.includes(o.id));

  const add = (id: string) => {
    if (id && !value.includes(id)) onChange([...value, id]);
    setInput("");
  };

  return (
    <div className="tag-editor">
      <span className="eyebrow tag-editor-label">{label}</span>
      <div className="tag-editor-chips">
        {value.map((id) => (
          <span key={id} className="tag-edit-chip mono">
            {labelFor(id)}
            <button type="button" aria-label={`Remove ${labelFor(id)}`} onClick={() => onChange(value.filter((x) => x !== id))}>
              <X size={11} />
            </button>
          </span>
        ))}
        {value.length === 0 && <span className="tag-edit-empty mono">none</span>}
      </div>
      <input
        className="tag-edit-add"
        list={listId}
        placeholder={`Add ${singular}…`}
        value={input}
        onChange={(e) => {
          // Selecting a datalist option fires change with the full label — add it immediately.
          const match = byLabel(e.target.value);
          if (match) add(match.id);
          else setInput(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const m = byLabel(input);
            if (m) add(m.id);
          }
        }}
      />
      <datalist id={listId}>
        {available.map((o) => (
          <option key={o.id} value={o.label} />
        ))}
      </datalist>
    </div>
  );
}
